import { IonButton, IonButtons, IonCard, IonCardContent, IonCol, IonContent, IonFab, IonFabButton, IonFooter, IonGrid, IonHeader, IonIcon, IonImg, IonInput, IonItem, IonLabel, IonMenuButton, IonNote, IonPage, IonRadio, IonRadioGroup, IonRefresher, IonRefresherContent, IonRow, IonSelect, IonSelectOption, IonSkeletonText, IonTextarea, IonTitle, IonToolbar, RefresherEventDetail, useIonAlert, useIonRouter, useIonViewDidEnter } from '@ionic/react';
import { alarmOutline, chevronBackOutline, gitCompareOutline, navigateOutline, removeCircleOutline, returnDownBackOutline, trashOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import 'react-initials-avatar/lib/ReactInitialsAvatar.css';
import { useAppContext } from '../AppProvider';

type Num = 1|2|3|4|5;

interface RouteParams {
    token: string;
}

const TrailEdit: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [route, setRoute] = useState<any>({});
    const [nameEdit, setNameEdit] = useState<boolean>(false);
    const [placesEdit, setPlacesEdit] = useState<boolean>(false);
    const [endPlaceDisabled, setEndPlaceDisabled] = useState<boolean>(true);   
    const [distanceEdit, setDistanceEdit] = useState<boolean>(false);
    const [catChoices, setCatChoices] = useState<Num[]>([]);
    const [waypointsEdit, setWaypointsEdit] = useState<boolean>(false);
    const [notesEdit, setNotesEdit] = useState<boolean>(false);

    const startPlaceRef = useRef<any>();
    const endPlaceRef = useRef<any>();

    const [showAlert] = useIonAlert();
    const { token } = useParams<RouteParams>();
    const router = useIonRouter();

    const { authKey, authenticateMember, authorizedMember, accountSetupPct, alarms, limits, profiles, settings, getPlaces, startPlaces, endPlaces, setEndPlaces, SWFullHorizLogo, SWTextDarkLogo, textLogoLoaded, fadeInTextLogo, showToast, nl2br } = useAppContext();

    //
    // Authenticate Member
    //
    useIonViewDidEnter(() => { 
        const authenticate = async () => { 
            const authenticated = await authenticateMember();         
            if(!authenticated.mt) router.push('/login', 'forward');
        };
        authenticate();
    });


    useEffect(() => {
        if (limits.places > 0) {
            const fetchData = async () => {
                await getRoute(token, authorizedMember.memberToken);
                setLoading(false);
            }
            fetchData();
        }
    }, [authorizedMember, limits]);

    
    useEffect(() => {
        // handle empty/null safely
        const arr = (route.category ?? '')
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
        .map((n: any) => Number(n) as Num);
        setCatChoices(arr);
    }, [route]);

    const toggleNameForm = () => setNameEdit(!nameEdit);
    const togglePlacesForm = () => {
        if(route.type == 'LOOP') setEndPlaces(startPlaces);
        else {
            let eps:any = [];
            startPlaces.forEach((item: any, index: any, arr: any) => {
                if(item.placeToken != route.startPlace) eps.push(item);
            });
            setEndPlaces(eps);
        }        
        setPlacesEdit(!placesEdit);
    };
    const toggleDistanceForm = () => setDistanceEdit(!distanceEdit);
    const toggleWaypointsForm = () => setWaypointsEdit(!waypointsEdit);
    const toggleNotesForm = () => setNotesEdit(!notesEdit);


    const getRoute = async (rt: string, mt: string) => {
        try {
            const formData = new FormData();
            formData.append('mt', mt);
            formData.append('rt', rt);
            const response = await fetch('https://api.besolowise.com/routes/get/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if(response.ok) {
                const obj = await response.json();
                
                if(typeof obj.id == 'number') {                    
                    setRoute(obj); 
                    setEndPlaceDisabled(obj.type == 'LOOP');
                    getPlaces(mt, limits.places);
                } else {
                    showToast({
                        message: 'Data Error',
                        duration: 2000,
                        color: 'danger',
                        position: 'middle'
                    });
                }
            } else {
                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                    position: 'middle'
                });
            }
        } catch(error) {
            showToast({
                message: 'Connection Error: ' + error + '. Refresh this page to try again.',
                duration: 3000,
                color: 'danger',
                position: 'middle'
            });
        }       
    };

    const toggleType = (e: any) => {
        route.type = e.detail.value;

        setEndPlaceDisabled(e.detail.value == 'LOOP');

        if(e.detail.value == 'LOOP') {
            setEndPlaces(startPlaces);
            route.endPlace = startPlaceRef.current.value;
        } else {
            let eps:any = [];
            startPlaces.forEach((item:any, index:number, arr:any) => {
                if(item.placeToken != startPlaceRef.current.value) eps.push(item);
            });
            setEndPlaces(eps);
            route.endPlace = '';
        }
    };


    const syncEndPlace = (e: any) => {
        route.startPlace = e.detail.value;
        
        if(endPlaceDisabled) {     
            setEndPlaces(startPlaces);            
            endPlaceRef.current.value = startPlaceRef.current.value;
            route.endPlace = e.detail.value;
        } else if(endPlaceRef.current.value == startPlaceRef.current.value) {
            let eps:any = [];
            startPlaces.forEach((item:any, index:number, arr:any) => {
                if(item.placeToken != startPlaceRef.current.value) eps.push(item);
            });
            setEndPlaces(eps);
            endPlaceRef.current.value = '';
        }
    };


    const updateRoute = async (e: any) => {
        e.preventDefault();
        const form = e.target; 
        const formName = form.name;
        const btn: any = document.getElementById(formName + 'Submit');
        btn.innerHTML = 'Saving...';
        btn.setAttribute('disabled', '');
        const formData = new FormData(form);
        formData.append('formName', formName);
        formData.append('token', token);
        formData.append('mt', authKey.mt);

        try {
            const response = await fetch('https://api.besolowise.com/routes/update/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if(response.ok) {
                const obj = await response.json(); 
                
                if(obj.success) {
                    getRoute(obj.rt, authKey.mt);
                    btn.innerHTML = 'Save';
                    btn.removeAttribute('disabled');
                    document.getElementById(formName + 'Cancel')?.click();
                    showToast({
                        message: 'SAVED ',
                        duration: 2000,
                        color: 'success',
                    });
                } else {
                    btn.innerHTML = 'Save';
                    btn.removeAttribute('disabled');
                    showToast({
                        message: 'Error: ' + obj.message,
                        duration: 3000,
                        color: 'danger',
                    });
                }
            } else {
                btn.innerHTML = 'Save';
                btn.removeAttribute('disabled');
                showToast({
                    message: 'Response Error',
                    duration: 3000,
                    color: 'danger',
                });
            }            
        } catch(error) {
            btn.innerHTML = 'Save';
            btn.removeAttribute('disabled');
            showToast({
                message: 'Connection Error: ' + error,
                duration: 3000,
                color: 'danger',
            });
        } 
    };

    const reverseRoute = () => {
        showAlert({
            header: 'Confirm!',
            message: 'Are you sure you want to CREATE A REVERSE COPY of this Route?',
            buttons: [
                {text: 'Cancel',
                 role: 'cancel',
                 cssClass: 'alert-button-cancel', 
                },
                {
                    text: 'Copy',
                    role: 'confirm',
                    cssClass: 'alert-button-confirm',
                    handler: async () => {
                        try {
                            const formData = new FormData();
                            formData.append('token', token);
                            formData.append('mt', authKey.mt);
                            const response = await fetch('https://api.besolowise.com/routes/reverse/', {
                                method: 'POST',
                                credentials: 'include',
                                body: formData
                            });

                            console.log(response);

                            if(response.ok) {
                                const obj = await response.json(); 

                                console.log(obj);
                                
                                if(obj.success) {
                                    router.push('/account/routes', 'back');
                                } else {
                                    showToast({
                                        message: 'Data error: ' + obj.message + '. Try again.',
                                        duration: 4000,
                                        color: 'danger',
                                    });
                                }
                            } else {
                                showToast({
                                    message: 'Response error. Try again.',
                                    duration: 3000,
                                    color: 'danger',
                                });
                            }
                        } catch(error) {
                            showToast({
                                message: 'Connection Error: ' + error + '. Try again.',
                                duration: 5000,
                                color: 'danger',
                            });
                        }  
                    }
                }
            ]
        })
    };

    const deleteRoute = () => {
        showAlert({
            header: 'Confirm!',
            message: 'Are you sure you want to REMOVE this Route?',
            buttons: [
                {text: 'Cancel',
                 role: 'cancel',
                 cssClass: 'alert-button-cancel', 
                },
                {
                    text: 'Remove',
                    role: 'confirm',
                    cssClass: 'alert-button-confirm',
                    handler: async () => {
                        try {
                            const formData = new FormData();
                            formData.append('token', token);
                            formData.append('mt', authKey.mt);
                            const response = await fetch('https://api.besolowise.com/routes/delete/', {
                                method: 'POST',
                                credentials: 'include',
                                body: formData
                            });
                            if(response.ok) {
                                const obj = await response.json(); 
                                
                                if(obj.success) {
                                    router.push('/account/routes', 'back');
                                } else {
                                    showToast({
                                        message: 'Data error: ' + obj.message,
                                        duration: 4000,
                                        color: 'danger',
                                    });
                                }
                            } else {
                                showToast({
                                    message: 'Response error.',
                                    duration: 2000,
                                    color: 'danger',
                                });
                            }
                        } catch(error) {
                            showToast({
                                message: 'Connection Error: ' + error,
                                duration: 2000,
                                color: 'danger',
                            });
                        }  
                    }
                }
            ]
        })
    };
    
    const typeText = (s: string) => {
        switch(s) {
            case 'ONE-WAY': return 'One-way'
            case 'LOOP': return 'Round-trip'       
            default:  return 'Unknown'
        }
    };

    const categoryText = (arr: any) => {
        var str = '';
        arr.forEach((n: number) => {
            if (str != '') str = str + ', '
            if (n === 1) str = str + 'Hiking/Backpacking'
            else if (n === 2) str = str + 'Horseback Riding'
            else if (n === 3) str = str + 'Moto Riding'
            else if (n === 4) str = str + 'Mtn Biking'
            else if (n === 5) str = str + 'Trail Running'
        });
        return str;
    };

    const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
        setLoading(true);
        setTimeout(async () => {
            await getRoute(token, authKey.mt);
            event.detail.complete();
            setLoading(false);
        }, 2000);
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonButton color={'tertiary'} routerDirection='back' routerLink='/account/routes'>Routes</IonButton>
                    </IonButtons>
                    <IonTitle><IonImg className={textLogoLoaded ? 'fade-in' : 'fade-out'} src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo} onIonImgDidLoad={fadeInTextLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding account-content-bg" id='RouteContent'>

                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                <IonGrid>
                    <IonRow>
                        <IonCol size='12'>
                            <h1><IonIcon className='m-hide' color={'secondary'} icon={navigateOutline}></IonIcon> 
                                {loading 
                                    ? <IonSkeletonText animated={true} style={{ width: '55%', margin: '0 auto' }}></IonSkeletonText>
                                    : <>{route.name}</>
                                }
                                <br /><span>Editing a Saved Trail</span>
                            </h1>                            
                        </IonCol>
                    </IonRow>
                </IonGrid>
                
                <IonCard>
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={toggleNameForm}><b>Trail Name or Number</b> <em className='required'>Required</em></IonLabel>
                        { nameEdit 
                            ? (<form name='nameForm' onSubmit={updateRoute}>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol size='12'>
                                                <IonInput fill='outline' name='routeName' placeholder='Name' type='text' value={route.name}></IonInput>
                                            </IonCol>                               
                                        </IonRow>
                                    </IonGrid>
                                    <IonButton className='ion-margin-top' color={'medium'} id='nameFormCancel' onClick={toggleNameForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='nameFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : (<>
                                {loading 
                                    ? (<p><IonSkeletonText animated={true} style={{ width: '30%' }}></IonSkeletonText></p>)
                                    : (<p onClick={toggleNameForm}>{route.name}</p>)
                                }
                                </>)
                        }
                    </IonCardContent>
                </IonCard>  

                <IonCard>
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={togglePlacesForm}><b>Starting & Ending Trailheads</b> <em className='required'>Required</em></IonLabel>
                        { placesEdit  
                            ? (<form name='placesForm' onSubmit={updateRoute}>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol size='12'>
                                                <IonRadioGroup name='routeType' onIonChange={toggleType} value={route.type}>
                                                    <IonItem lines='none'>
                                                        <IonRadio justify="start" key={1} labelPlacement="end" value='LOOP'>
                                                            Round-trip<br />
                                                            <IonNote>Starts and ends at the same Trailhead</IonNote>
                                                        </IonRadio>
                                                    </IonItem>
                                                    <IonItem lines='none'>
                                                        <IonRadio justify="start" key={2} labelPlacement="end" value='ONE-WAY'>
                                                            One-way<br />
                                                            <IonNote>Starts and ends at different Trailheads</IonNote>
                                                        </IonRadio>
                                                    </IonItem>
                                                </IonRadioGroup>
                                            </IonCol>
                                                                              
                                        </IonRow>
                                        <IonRow>
                                            <IonCol size='12'>
                                                <IonSelect className='ion-margin-bottom' fill='outline' label='Starting Trailhead' labelPlacement='stacked' name='routeStartPlace' onIonChange={syncEndPlace} placeholder='Select the Starting Trailhead' value={route.startPlace} ref={startPlaceRef}>
                                                    {startPlaces
                                                        .filter((p: any) => p.placeSaved == '1' || p.placeToken == route.startPlace)
                                                        .map((place:any, index:number) => (
                                                        <IonSelectOption key={index} value={place.placeToken}>{place.placeName !== '' ? place.placeName : 'Unnamed'}</IonSelectOption>
                                                    ))}                                            
                                                </IonSelect>
                                            </IonCol>
                                            <IonCol size='12'>
                                                <IonSelect className='ion-margin-bottom' disabled={endPlaceDisabled} fill='outline' label='Ending Trailhead' labelPlacement='stacked' name='routeEndPlace' placeholder='Select the Ending Trailhead' value={route.endPlace} ref={endPlaceRef}>
                                                    {endPlaces
                                                        .filter((p: any) => p.placeSaved == '1' || p.placeToken == route.endPlace)
                                                        .map((place:any, index:number) => (
                                                        <IonSelectOption key={index} value={place.placeToken}>{place.placeName}</IonSelectOption>
                                                    ))}                                            
                                                </IonSelect>
                                            </IonCol>
                                            </IonRow>
                                    </IonGrid>
                                    <IonButton className='ion-margin-top' color={'medium'} id='placesFormCancel' onClick={togglePlacesForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='placesFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : (<>
                                {loading 
                                    ? (<p><IonSkeletonText animated={true} style={{ width: '60%' }}></IonSkeletonText></p>)
                                    : (<>
                                        <p onClick={togglePlacesForm}>{route.startPlaceName == '' ? 'Unnamed' : (route.startPlaceName + ' to ' + route.endPlaceName)}</p>
                                        <p>{typeText(route.type)}</p>
                                       </>)
                                }
                                </>) 
                        }
                    </IonCardContent>
                </IonCard>
                
                <IonCard>
                    <IonCardContent>
                    <IonLabel color={'dark'} onClick={toggleDistanceForm}><b>Approximate Total Distance</b></IonLabel>
                        { distanceEdit 
                            ? (<form name='distanceForm' onSubmit={updateRoute}>
                                    <IonGrid>
                                        <IonRow>
                                            <IonCol size='6'>
                                                <IonInput fill='outline' name='routeDistance' placeholder='Distance' type='number' step='.1' value={route.distance}></IonInput>
                                            </IonCol>
                                            <IonCol size='6'>
                                                <IonSelect fill='outline' labelPlacement='stacked' name='routeDistanceUnits' placeholder='Units' value={route.distanceUnits}>  
                                                    <IonSelectOption value='MI'>MI</IonSelectOption>
                                                    <IonSelectOption value='KM'>KM</IonSelectOption>
                                                </IonSelect>
                                            </IonCol>
                                            <IonCol size='12'>
                                                <IonSelect fill='outline' label='Travel Modes' labelPlacement='stacked' name='routeCategory' placeholder='You can select multiple' value={catChoices} multiple={true}>
                                                    <IonSelectOption value={1}>Hiking/Backpacking</IonSelectOption>
                                                    <IonSelectOption value={2}>Horseback Riding</IonSelectOption>
                                                    <IonSelectOption value={3}>Moto Riding</IonSelectOption>
                                                    <IonSelectOption value={4}>Mtn Biking</IonSelectOption>
                                                    <IonSelectOption value={5}>Trail Running</IonSelectOption>
                                                </IonSelect>
                                            </IonCol>                                   
                                        </IonRow>
                                    </IonGrid>
                                    <IonButton className='ion-margin-top' color={'medium'} id='distanceFormCancel' onClick={toggleDistanceForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='distanceFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : (<>
                                {loading 
                                    ? (<p><IonSkeletonText animated={true} style={{ width: '20%' }}></IonSkeletonText></p>)
                                    : (<p onClick={toggleDistanceForm}>{route.distance == '' || route.distance == '0.0' ? 'Not Recorded' : route.distance + ' ' + route.distanceUnits + ' via ' + categoryText(catChoices)}</p>)
                                }
                                </>)
                        }
                    </IonCardContent>
                </IonCard>                 
                
                <IonCard>
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={toggleWaypointsForm}><b>Trail Description & Waypoints</b></IonLabel><br />
                        { waypointsEdit  
                            ? (<>
                                <IonNote>This can be a list of points or landmarks along this Trail, such as a peak, a lake, etc.</IonNote>
                                <form name='waypointsForm' onSubmit={updateRoute}> 
                                    <IonTextarea autoGrow={true} fill='outline' name='routeWaypoints' placeholder='' value={route.waypoints}></IonTextarea> 
                                    <IonButton className='ion-margin-top' color={'medium'} id='waypointsFormCancel' onClick={toggleWaypointsForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='waypointsFormSubmit' type='submit'>Save</IonButton>
                                </form>
                               </>)                                                     
                            : (<>
                                {loading 
                                    ? (<p><IonSkeletonText animated={true} style={{ width: '60%' }}></IonSkeletonText></p>)
                                    : (<p onClick={toggleWaypointsForm}>{route.waypoints == '' ? 'Not Recorded' : (<span dangerouslySetInnerHTML={{ __html: nl2br(route.waypoints) }} />)}</p>)
                                }
                                </>) 
                        }
                    </IonCardContent>
                </IonCard>

                <IonCard>
                    <IonCardContent>
                        <IonLabel color={'dark'} onClick={toggleNotesForm}><b>Additional Information</b></IonLabel>
                        { notesEdit  
                            ? (<form name='notesForm' onSubmit={updateRoute}> 
                                    <IonTextarea autoGrow fill='outline' name='routeNotes' value={route.notes}></IonTextarea>
                                    <IonButton className='ion-margin-top' color={'medium'} id='notesFormCancel' onClick={toggleNotesForm}>Cancel</IonButton>
                                    <IonButton className='ion-margin-top' color={'secondary'} id='notesFormSubmit' type='submit'>Save</IonButton>
                                </form>)                                                     
                            : (<>
                                {loading 
                                    ? (<p><IonSkeletonText animated={true} style={{ width: '60%' }}></IonSkeletonText></p>)
                                    : (<p onClick={toggleNotesForm}>{route.notes == '' ? 'Not Recorded' : (<span dangerouslySetInnerHTML={{ __html: nl2br(route.notes) }} />)}</p>)
                                }
                                </>) 
                        }
                    </IonCardContent>
                </IonCard>

                <div className='ion-text-center'>
                    {accountSetupPct === 100 && alarms < limits.timers &&
                        <IonButton className='ion-margin' color={'tertiary'} fill="outline" routerLink={`/account/timer/create/?route=${token}`} routerDirection='forward'><IonIcon icon={alarmOutline} slot='start'></IonIcon> Add to a New Timer</IonButton>
                    }
                    {route.type == 'ONE-WAY' && profiles?.routes < limits?.routes &&
                        <IonButton className='ion-margin' color={'tertiary'} fill="outline" onClick={reverseRoute}><IonIcon icon={gitCompareOutline} slot='start'></IonIcon> Create a Reverse Copy</IonButton>
                    }
                    <IonButton className='ion-margin delete-button-outline' color={'remove'} fill="outline" onClick={deleteRoute}><IonIcon icon={trashOutline} slot='start'></IonIcon> Remove This Trail</IonButton>
                </div>

            </IonContent>
        </IonPage>
    );
};

export default TrailEdit;
